import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { RootState, useAppDispatch } from '../services/store';
import { extractOrderHashFromPath } from '../utilities/helpers';
import { EymemoryRoutes } from '../navigation/routes';
import { EymemoryLoginPersistActions, eymemoryLogout, updateEymemoryLoginStatus, validateEymemoriesLoginTokens } from 'src/shared/redux-state/src';

interface UseEymemoryAuthProps {
  store: any;
  setValidatingTokens: (value: boolean) => void;
}

export const useEymemoryAuth = ({ store, setValidatingTokens }: UseEymemoryAuthProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const initializeEymemorySession = useCallback(async () => {
    const orderHashData = location?.state?.orderHash || extractOrderHashFromPath(location.pathname);
    const loginState = (store.getState() as RootState)?.eymemoryPersistLogin?.[orderHashData]?.loginData;

    const redirectToError = () => {
      const path = `${EymemoryRoutes.EYMEMORIES_ERROR}${orderHashData ? `?orderHash=${orderHashData}` : ''}`;
      navigate(path);
    };

    if (!loginState || Object.keys(loginState).length === 0 || !loginState?.accessToken) {
      setValidatingTokens(false);
      await dispatch(EymemoryLoginPersistActions.resetPersistLoginState({ orderHash: orderHashData }));
      redirectToError();
      return;
    }

    setValidatingTokens(true);
    try {
      const result = await dispatch(validateEymemoriesLoginTokens({ orderHash: orderHashData, reduxStore: store })).unwrap();
      if (result) {
        await dispatch(updateEymemoryLoginStatus({ orderHash: orderHashData, isLoggedIn: true }));
        setValidatingTokens(false);
      } else {
        await dispatch(eymemoryLogout({ orderHash: orderHashData }));
        setValidatingTokens(false);
        redirectToError();
      }
    } catch {
      setValidatingTokens(false);
      redirectToError();
    }
  }, [dispatch, location, navigate, setValidatingTokens, store]);

  return { initializeEymemorySession };
};
